import {
  EditUserProps,
  EditUserFormInputs,
  Response,
  EditUserInputs,
  EditTestUserInputs,
} from "./types";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Stack,
  Chip,
  MenuItem,
  Snackbar,
  Alert,
  Checkbox,
} from "@mui/material";
import { Select as MuiSelect } from "@mui/material";
import { style, padding } from "./styles";
import { convertActivationStatus } from "transformers/account.transformer";
import { TimeZone, TIME_ZONES } from "config";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  QUERY_RESPONSE,
  UPDATE_SINGLE_ACCOUNT,
  UPDATE_SINGLE_USER,
} from "./queries";
import { useMutation, useQuery } from "@apollo/client";
import { Account } from "types/api";
import { useEffect, useState } from "react";
import {
  ActivityPhoneInput,
  ContactPhoneInput,
  PhoneEntry,
} from "../../../../../Users/components/AddUserForm/phoneInput";
import { parsePhoneNumber } from "react-phone-number-input";
import { DateTimePickerComponent } from "../../../Playlists/components/DateTimePicker";
import { LeadOptions } from "../../types";
import { Option } from "types/utils";
import Select from "react-select";
import { UserModalContainer } from "styles/flex";

export const EditUserModal = (props: EditUserProps) => {
  const [contactCountry, setContactCountry] = useState("US");
  const [contactPhone, setContactPhone] = useState<string | undefined>("");
  const [activityCountry, setActivityCountry] = useState("US");
  const [activityPhone, setActivityPhone] = useState<string | undefined>("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [disableButton, setDisableButton] = useState<boolean>(false);

  const account = props?.account;
  const [bdlStartDateValue, setBdlStartDateValue] = useState<Date | null>(
    account?.bdlProgramStartOn ?? null
  );
  const [bdlEndDateValue, setBdlEndDateValue] = useState<Date | null>(
    account?.bdlProgramEndOn ?? null
  );
  const navigate = useNavigate();

  // add dependency for edit modal open - resets to db values when edit discarded
  useEffect(() => {
    setActivityPhone(account?.activityPhoneNumber);
    setContactPhone(account?.contactPhoneNumber);
    setActivityCountry(
      parsePhoneNumber(account?.activityPhoneNumber ?? "")?.country ?? "US"
    );
    setContactCountry(
      parsePhoneNumber(account?.contactPhoneNumber ?? "")?.country ?? "US"
    );
  }, [account.activityPhoneNumber, account.contactPhoneNumber, props.open]);

  const handleChangeBdlStartDate = (newValue: Date | null) => {
    if (newValue) {
      const date = new Date(newValue);
      setBdlStartDateValue(date);
      setValue("bdlProgramStartOn", date);
    } else {
      setBdlStartDateValue(null);
      setValue("bdlProgramStartOn", null);
    }
  };

  const handleChangeBdlEndDate = (newValue: Date | null) => {
    if (newValue) {
      const endDateOnly = newValue?.toISOString()?.split("T")[0];
      const endDateTime = endDateOnly?.concat("T23:59:59.999Z");
      const date = new Date(endDateTime);
      const endbdlDate = new Date(date);

      setBdlEndDateValue(endbdlDate);
      setValue("bdlProgramEndOn", endbdlDate);
    } else {
      setBdlEndDateValue(null);
      setValue("bdlProgramEndOn", null);
    }
  };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    reset,
  } = useForm<EditUserFormInputs>({
    mode: "onChange",
    defaultValues: {
      firstName: account.firstName,
      lastName: account.lastName,
      specialistId: account?.specialist?.id,
      timeZone: account.timeZone,
      summary: account.summary,
      isTestUser: account?.user?.isTestUser,
      bdlProgramStartOn: account?.bdlProgramStartOn ?? null,
      bdlProgramEndOn: account?.bdlProgramEndOn ?? null,
      leadType: account?.leadType,
      Id_Ehr: account?.Id_Ehr,
    },
  });

  const { data } = useQuery<Response>(QUERY_RESPONSE, {
    fetchPolicy: "cache-and-network",
  });

  const specialists = [...(data?.specialists ?? [])]
    .filter((x) => !!x)
    .sort((a: Account, b: Account) => a.firstName.localeCompare(b.firstName));

  const ButtonLogic = () => {
    const disabled =
      !isValid || !contactPhone || !activityPhone || disableButton;
    return (
      <Button
        type="submit"
        variant="contained"
        disabled={disabled}
        className="button"
      >
        Submit
      </Button>
    );
  };

  const [updateAccount, { error: accountError }] = useMutation<EditUserInputs>(
    UPDATE_SINGLE_ACCOUNT
  );

  const [updateTestUser, { error: userError }] =
    useMutation<EditTestUserInputs>(UPDATE_SINGLE_USER);

  useEffect(() => {
    if (accountError || userError) {
      const msg = [accountError?.message, userError?.message].join(". ");
      setErrorMessage(msg);
    }
  }, [accountError, userError]);

  const onSubmit: SubmitHandler<EditUserFormInputs> = async (response) => {
    let success = true;
    try {
      await updateAccount({
        variables: {
          id: account?.id,
          firstName: response.firstName,
          lastName: response.lastName,
          contactPhoneNumber: contactPhone,
          activityPhoneNumber: activityPhone,
          timeZone: response.timeZone,
          summary: response.summary,
          specialistId: response.specialistId,
          bdlProgramStartOn: response.bdlProgramStartOn,
          bdlProgramEndOn: response.bdlProgramEndOn,
          leadType: response.leadType,
          Id_Ehr: response.Id_Ehr,
        },
      });
    } catch (error) {
      console.error("Failed to update account");
      success = false;
    }
    try {
      const userId = account?.user?.id;
      if (userId) {
        await updateTestUser({
          variables: {
            userId,
            firstName: response.firstName,
            lastName: response.lastName,
            isTestUser: response.isTestUser,
          },
        });
      }
    } catch (error) {
      console.error("Failed to update user");
      success = false;
    }
    if (success) {
      props.onClose();
      navigate("/refresh");
    }
  };

  return (
    <>
      <Modal
        open={props.open}
        onClose={() => {
          props.onClose(), reset();
        }}
      >
        <UserModalContainer>
          <Box sx={style}>
            <Typography component="span">
              <Grid>
                <Grid container justifyContent="space-between">
                  <Typography variant="h4">Patient Info</Typography>
                  <Button
                    className="button"
                    variant="outlined"
                    onClick={() => {
                      props.onClose(), reset();
                    }}
                  >
                    X
                  </Button>
                </Grid>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container>
                    <Grid item xs={3} sx={padding}>
                      <Stack>
                        <label>Patient First Name*</label>
                        <Controller
                          render={({ field }) => <TextField {...field} />}
                          name="firstName"
                          control={control}
                          rules={{ required: true }}
                        />
                        <strong
                          style={{
                            color: "#d32f2f",
                            fontSize: "0.75rem",
                            fontFamily:
                              '"Roboto","Helvetica","Arial",sans-serif',
                            fontWeight: 400,
                          }}
                        >
                          {errors?.firstName && "First name is required"}
                        </strong>
                      </Stack>
                    </Grid>
                    <Grid item xs={3} sx={padding}>
                      <Stack>
                        <label>Patient Last Name*</label>
                        <Controller
                          render={({ field }) => <TextField {...field} />}
                          name="lastName"
                          control={control}
                          rules={{ required: true }}
                        />
                        <strong
                          style={{
                            color: "#d32f2f",
                            fontSize: "0.75rem",
                            fontFamily:
                              '"Roboto","Helvetica","Arial",sans-serif',
                            fontWeight: 400,
                          }}
                        >
                          {errors?.lastName && "Last name is required"}
                        </strong>
                      </Stack>
                    </Grid>
                    <Grid item xs={3} sx={padding}>
                      <Stack>
                        <label>EHR Id*</label>
                        <Controller
                          render={({ field: { onChange, value, ...rest } }) => (
                            <TextField
                              {...rest}
                              onChange={(e) => {
                                const alphaNumericValue =
                                  e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                                onChange(alphaNumericValue);
                              }}
                              value={value}
                            />
                          )}
                          name="Id_Ehr"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: "EHR ID is required",
                            },
                            pattern: {
                              value: /^[a-zA-Z0-9]+$/,
                              message: "EHR ID must be alphanumeric",
                            },
                          }}
                        />
                        <strong
                          style={{
                            color: "#d32f2f",
                            fontSize: "0.75rem",
                            fontFamily:
                              '"Roboto","Helvetica","Arial",sans-serif',
                            fontWeight: 400,
                          }}
                        >
                          {errors?.Id_Ehr &&
                            (errors.Id_Ehr.type === "required"
                              ? "EHR ID is required"
                              : errors.Id_Ehr.type === "pattern"
                              ? "EHR ID must be alphanumeric"
                              : "")}
                        </strong>
                      </Stack>
                    </Grid>
                    <Grid item xs={3} sx={padding}>
                      <Stack>
                        <label>Moneta Therapist*</label>
                        <Controller
                          render={({ field }) => (
                            <MuiSelect {...field}>
                              {specialists
                                .filter((x) => !!x)
                                .map((specialist: Account, index: number) => (
                                  <MenuItem key={index} value={specialist.id}>
                                    {specialist.firstName}
                                  </MenuItem>
                                ))}
                            </MuiSelect>
                          )}
                          name="specialistId"
                          control={control}
                          rules={{ required: true }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={3} sx={padding}>
                      <Stack>
                        <label>Patient Status</label>
                        <Grid item>
                          <Typography component="span">
                            <Chip
                              label={convertActivationStatus(
                                props.account?.deletedAt
                              )}
                              variant="outlined"
                            />
                          </Typography>
                        </Grid>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={3} sx={padding}>
                      <Stack>
                        <label>Time Zone*</label>
                        <Controller
                          render={({ field }) => (
                            <MuiSelect {...field}>
                              {TIME_ZONES.map((timeZone: TimeZone) => (
                                <MenuItem
                                  key={timeZone.value}
                                  value={timeZone.value}
                                >
                                  {timeZone.name}
                                </MenuItem>
                              ))}
                            </MuiSelect>
                          )}
                          name="timeZone"
                          control={control}
                          rules={{ required: true }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={3} sx={padding}>
                      <label>Is Test Patient</label>
                      <Controller
                        name="isTestUser"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <Checkbox
                              onChange={(e) => field.onChange(e.target.checked)}
                              checked={field.value}
                            />
                            <label>Check box if test patient</label>
                          </div>
                        )}
                      />
                    </Grid>
                    <Grid container>
                      <Grid item xs={12} sx={padding}>
                        <Stack>
                          <label>Lead Type</label>

                          <Controller
                            name="leadType"
                            control={control}
                            // rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                              <div>
                                <Select
                                  options={LeadOptions}
                                  value={LeadOptions.find((option) =>
                                    option?.value === value ? option?.label : ""
                                  )}
                                  defaultValue={{
                                    label:
                                      account?.leadType === "None"
                                        ? "None"
                                        : "", // Set default label to "None" if leadType is "None"
                                    value: account?.leadType || "None", // Set default value to leadType if it exists, otherwise "None"
                                  }}
                                  onChange={(
                                    selectedOption: Partial<Option<any>> | null
                                  ) => {
                                    const newFeature = selectedOption?.value;
                                    setValue("leadType", newFeature);
                                    onChange(newFeature);
                                  }}
                                />
                              </div>
                            )}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                    <Grid item xs={3} sx={padding}>
                      <Stack>
                        <label>Email*</label>
                        <Typography>
                          {account?.user?.email ?? "No email on file"}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid container style={{ padding: "8px" }}>
                      <Grid container spacing={4}>
                        <Grid item xs={4} sx={padding}>
                          <Stack>
                            <PhoneEntry
                              country={activityCountry}
                              setCountry={setActivityCountry}
                              phoneNumber={activityPhone}
                              setPhoneNumber={setActivityPhone}
                              phoneInputComponent={ActivityPhoneInput}
                              setDisableButton={setDisableButton}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={4} sx={padding}>
                          <Stack>
                            <PhoneEntry
                              country={contactCountry}
                              setCountry={setContactCountry}
                              phoneNumber={contactPhone}
                              setPhoneNumber={setContactPhone}
                              phoneInputComponent={ContactPhoneInput}
                              setDisableButton={setDisableButton}
                            />
                          </Stack>
                        </Grid>
                        {/* <Grid item sx={padding}>
                          <Controller
                            name="bdlProgramStartOn"
                            control={control}
                            rules={{ required: false }}
                            render={({ field: { ref, ...rest } }) => (
                              <DateTimePickerComponent
                                dateValue={bdlStartDateValue}
                                handleChange={handleChangeBdlStartDate}
                                label={"BDL Program Start Date"}
                                required={false}
                                {...rest}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item sx={padding}>
                          <Controller
                            name="bdlProgramEndOn"
                            control={control}
                            rules={{ required: false }}
                            render={({ field: { ref, ...rest } }) => (
                              <DateTimePickerComponent
                                dateValue={bdlEndDateValue}
                                handleChange={handleChangeBdlEndDate}
                                label={"BDL Program End Date"}
                                required={false}
                                {...rest}
                              />
                            )}
                          />
                        </Grid> */}
                      </Grid>
                      <Grid container style={{ marginTop: "25px" }}>
                        <ButtonLogic />
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              </Grid>
            </Typography>
          </Box>
        </UserModalContainer>
      </Modal>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={!!errorMessage}
        onClose={() => setErrorMessage(undefined)}
      >
        <Alert onClose={() => setErrorMessage(undefined)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
